import React, { FC, Fragment, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Box, Button, CircularProgress, Grid, Skeleton, TextField } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { FileSource } from 'models/file';

/** The media both profiles share, plus the white-label login colours. */
export type ProfileMedia = {
    logoImage: string;
    staffImage: string;
    logoSignedUrl: string;
    staffSignedUrl: string;
    hexCode: string;
    textCode: string;
};

type GeneralProfileTabProps = {
    media: ProfileMedia;
    onChange: (media: ProfileMedia) => void;
};

type ImageKind = 'logo' | 'staff';

/** DD-3674: tall images are shown scaled down to this height, keeping their proportions. */
const SECONDARY_IMAGE_MAX_HEIGHT = 300;

const ImagePreview: FC<{ isUploading: boolean; url: string; alt: string; maxHeight?: number }> = ({
    isUploading,
    url,
    alt,
    maxHeight,
}) => {
    if (isUploading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
            </Box>
        );
    }
    if (url) {
        return (
            <img style={{ maxWidth: '100%', maxHeight, objectFit: 'contain', display: 'block' }} src={url} alt={alt} />
        );
    }
    return <Skeleton variant="rectangular" sx={{ width: '100%', height: '250px' }} animation={false} />;
};

/**
 * DD-3662: logo and image, shown on both the clinic and the supplier profile, and the
 * white-label login colours. Address and contact are edited under General information.
 */
const GeneralProfileTab: FC<GeneralProfileTabProps> = ({ media, onChange }) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const currentOrganisation = currentOrgVar();
    const logoInputRef = useRef<HTMLInputElement | null>(null);
    const staffInputRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState<ImageKind>();
    const [isInvalidFile, setIsInvalidFile] = useState(false);
    const isWhiteLabelEnabled = customerSettings.getCustomerSetting(
        currentOrganisation.customerSettings,
        CONSTANTS.SETTINGS_NAMES.WHITE_LABEL,
    );

    const showInvalidFile = () => {
        setIsInvalidFile(true);
        setTimeout(() => setIsInvalidFile(false), 500);
    };

    const uploadImage = async (kind: ImageKind, input: HTMLInputElement | null) => {
        const file = input?.files?.[0];
        setIsInvalidFile(false);
        if (!file || !file.type.startsWith('image/')) {
            showInvalidFile();
            return;
        }
        try {
            setUploading(kind);
            const formData = new FormData();
            formData.append('files', file, file.name);
            const response = await axios.post(
                `${CONSTANTS.DOC_API_URI}/upload/files?directory=${'organizations/' + currentOrganisation._id + '/image'}&source=${FileSource.SETTINGS}`,
                formData,
                { headers: { Authorization: Cookies.get(CONSTANTS.TOKEN_KEY) } },
            );
            const uploaded = response.status === 200 ? response.data?.[0] : undefined;
            if (uploaded) {
                onChange(
                    kind === 'logo'
                        ? { ...media, logoImage: uploaded._id, logoSignedUrl: uploaded.url }
                        : { ...media, staffImage: uploaded._id, staffSignedUrl: uploaded.url },
                );
            }
        } catch (error) {
            showInvalidFile();
        } finally {
            setUploading(undefined);
        }
    };

    const logInLink = `${CONSTANTS.AUTH_FRONTEND_URL}/login?labId=${currentOrganisation._id}`;

    return (
        <Fragment>
            {isInvalidFile ? (
                <AlertSnackbar type="error" message={translate('lab-settings:INVALID_FILE_FORMAT')} />
            ) : undefined}
            <Box my={3}>
                <Grid container spacing={2}>
                    <Grid item sm={12} md={4}>
                        <PageTitle headingStyle="h3" title={translate('lab-settings:LOGO')} />
                        <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={logoInputRef}
                            onChange={() => uploadImage('logo', logoInputRef.current)}
                        />
                        <Button onClick={() => logoInputRef.current?.click()}>
                            {translate('lab-settings:UPLOAD_IMAGE')}
                        </Button>
                        <ImagePreview isUploading={uploading === 'logo'} url={media.logoSignedUrl} alt="Company Logo" />
                    </Grid>
                    <Grid item sm={12} md={8}>
                        <PageTitle headingStyle="h3" title={translate('lab-settings:ADDITIONAL_IMAGE')} />
                        <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={staffInputRef}
                            onChange={() => uploadImage('staff', staffInputRef.current)}
                        />
                        <Button onClick={() => staffInputRef.current?.click()}>
                            {translate('lab-settings:UPLOAD_IMAGE')}
                        </Button>
                        <ImagePreview
                            isUploading={uploading === 'staff'}
                            url={media.staffSignedUrl}
                            alt="Additional image"
                            maxHeight={SECONDARY_IMAGE_MAX_HEIGHT}
                        />
                    </Grid>
                </Grid>
            </Box>
            {isWhiteLabelEnabled ? (
                <Fragment>
                    <PageTitle headingStyle="h3" title={translate('lab-settings:MY_PAGES')} />
                    <Box mb={2}>
                        <Grid container spacing={2}>
                            <Grid item sm={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={translate('lab-settings:BACKGROUND_COLOR')}
                                    placeholder={translate('lab-settings:COLOR_PLACEHOLDER')}
                                    value={media.hexCode}
                                    onChange={(event) => onChange({ ...media, hexCode: event.target.value })}
                                />
                            </Grid>
                            <Grid item sm={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={translate('lab-settings:TEXT_COLOR')}
                                    placeholder={translate('lab-settings:COLOR_PLACEHOLDER')}
                                    value={media.textCode}
                                    onChange={(event) => onChange({ ...media, textCode: event.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box mb={3}>
                        <Button component="a" href={logInLink} target="_blank" rel="noopener noreferrer">
                            {translate('lab-settings:SHOW_LOGIN_PAGE')}
                        </Button>
                    </Box>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default GeneralProfileTab;
